/*
 * Apply a recommendation from either a product-page sizing guide or the
 * Shape & Size Advisor handoff. The receiver waits for asynchronously loaded
 * product controls, applies fields in dependency order, and reports unmatched
 * values without claiming that every visible option succeeded.
 */
(function () {
    const transferKeys = [
        'kindred_handoff',
        'kindred_shape',
        'kindred_product_id',
        'kindred_function',
        'kindred_size',
        'kindred_width',
        'kindred_flex',
        'kindred_construction'
    ];
    const params = new URLSearchParams(window.location.search);
    const capturedQuery = window.KindredProductHandoffQuery;
    if (capturedQuery && capturedQuery.origin === window.location.origin &&
        capturedQuery.pathname === window.location.pathname && typeof capturedQuery.query === 'string' &&
        (params.get('kindred_handoff') === null || params.get('kindred_handoff') === '1')) {
        const capturedParams = new URLSearchParams(capturedQuery.query);
        if (capturedParams.get('kindred_handoff') === '1') {
            transferKeys.forEach(function (key) {
                // A theme or app can replace the URL before this deferred asset
                // executes. Current URL choices always outrank the early capture.
                if (!params.has(key) && capturedParams.has(key)) params.set(key, capturedParams.get(key));
            });
        }
    }
    const hasUrlHandoff = params.get('kindred_handoff') === '1';
    const expectedProductId = params.get('kindred_product_id');
    const urlRecommendation = {
        Function: semanticString(params.get('kindred_function')),
        Size: semanticString(params.get('kindred_size')),
        Width: semanticString(params.get('kindred_width')),
        Construction: semanticString(params.get('kindred_construction')),
        'Flex Pattern': semanticString(params.get('kindred_flex'))
    };

    let firstApplied = null;
    let urlComplete = false;
    let urlInFlight = null;
    let urlAttempts = 0;
    let urlRetryTimer = null;
    let urlRepairAttempts = 0;
    let urlMaintenanceDeadline = 0;
    let urlAbortController = null;
    let incomingStatus = hasUrlHandoff ? 'pending' : '';
    let incomingResult = null;
    let incomingRetired = false;
    let receiverChoiceDepth = 0;
    const recommendationStorageKey = 'kindred-product-recommendation-v1';
    const recommendationLifetime = 30 * 60 * 1000;
    const selectionKeys = [
        'shape', 'size', 'destination', 'productId', 'image', 'resultId',
        'width', 'widthLabel', 'widthValue', 'widthWaist', 'widthAdjustment',
        'widthFitMode', 'widthSuggestionReason', 'widthExplicit',
        'construction', 'constructionLabel', 'constructionValue', 'constructionExplicit',
        'flex', 'flexLabel', 'flexExplicit', 'functionValue'
    ];
    const answerKeys = [
        'product_type', 'collection', 'ski_style', 'unit', 'height', 'height_feet', 'height_inches',
        'weight', 'boot_size_unit', 'boot_size', 'ability', 'location',
        'stance_width_unit', 'stance_width', 'front_angle', 'rear_angle',
        'ski_bsl', 'ski_binding_style', 'terrain', 'snow', 'turn_style',
        'responsiveness', 'ride_feel', 'touring_focus', 'ski_touring_notch', 'priorities'
    ];
    let incomingBundle = null;
    const activeApplications = new Map();
    const toolWrappers = '.kindred-sizing-tool, [data-kindred-tool], ' +
        '[data-custom-build-intake], [data-kindred-shape-size-advisor], ' +
        '[data-kindred-product-sizing], [data-kindred-shape-finder], ' +
        '[data-kindred-shape-finder-root]';

    function semanticString(value) {
        if (typeof value !== 'string' && typeof value !== 'number') return '';
        const text = String(value).trim();
        if (!text || text.length > 240 || /[\u0000-\u001f\u007f]/.test(text) ||
            /^(?:awaiting product options|loading(?: suggestions| options)?[….]*|not selected|—)$/i.test(text)) return '';
        return text;
    }

    function cleanRecord(source, keys) {
        const result = {};
        if (!source || typeof source !== 'object' || Array.isArray(source)) return result;
        keys.forEach(function (key) {
            if (!Object.prototype.hasOwnProperty.call(source, key)) return;
            const value = source[key];
            if (key === 'priorities' && Array.isArray(value)) {
                result[key] = unique(value.map(semanticString)).slice(0, 3);
            } else if (typeof value === 'boolean') {
                result[key] = value;
            } else if (typeof value === 'number' && Number.isFinite(value)) {
                result[key] = value;
            } else {
                const text = semanticString(value);
                if (text) result[key] = text;
            }
        });
        return result;
    }

    function productDestination(value) {
        try {
            const destination = new URL(value, window.location.href);
            return destination.origin === window.location.origin && /^\/products\/[^/]+\/?$/.test(destination.pathname)
                ? destination : null;
        } catch (error) { return null; }
    }

    function carryRecommendation(selection, state, destination) {
        const cleanedSelection = cleanRecord(selection, selectionKeys);
        const target = productDestination(destination || cleanedSelection.destination);
        if (!target || !cleanedSelection.shape || !cleanedSelection.size) return null;
        cleanedSelection.destination = target.href;
        const bundle = {
            version: 1,
            createdAt: Date.now(),
            origin: target.origin,
            pathname: target.pathname.replace(/\/$/, ''),
            selection: cleanedSelection,
            state: cleanRecord(state, answerKeys)
        };
        try { window.sessionStorage.setItem(recommendationStorageKey, JSON.stringify(bundle)); }
        catch (error) { /* URL fields still carry the product choices when optional session storage is blocked. */ }
        return JSON.parse(JSON.stringify(bundle));
    }

    function readIncomingBundle() {
        if (!hasUrlHandoff) return null;
        try {
            const saved = JSON.parse(window.sessionStorage.getItem(recommendationStorageKey) || 'null');
            if (!saved || saved.version !== 1 || !Number.isFinite(saved.createdAt) ||
                Date.now() - saved.createdAt < 0 || Date.now() - saved.createdAt > recommendationLifetime ||
                saved.origin !== window.location.origin || saved.pathname !== window.location.pathname.replace(/\/$/, '')) return null;
            const selection = cleanRecord(saved.selection, selectionKeys);
            if (expectedProductId && String(selection.productId || '') !== String(expectedProductId)) return null;
            if (semanticString(params.get('kindred_shape')) && selection.shape !== semanticString(params.get('kindred_shape'))) return null;
            return { selection: selection, state: cleanRecord(saved.state, answerKeys) };
        } catch (error) { return null; }
    }

    function incomingRecommendation(productId) {
        if (!hasUrlHandoff || incomingRetired ||
            (productId && expectedProductId && String(productId) !== String(expectedProductId))) return null;
        if (productId && incomingBundle && incomingBundle.selection.productId && String(productId) !== String(incomingBundle.selection.productId)) return null;
        const selection = Object.assign({}, incomingBundle && incomingBundle.selection || {});
        const fieldKeys = {
            Size: ['size'], Width: ['widthValue', 'widthLabel'], Construction: ['constructionValue', 'constructionLabel'],
            'Flex Pattern': ['flexLabel'], Function: ['functionValue']
        };
        Object.keys(fieldKeys).forEach(function (name) {
            const value = semanticString(urlRecommendation[name]);
            const savedValue = selection[fieldKeys[name][0]];
            if (savedValue && value !== String(savedValue)) {
                if (name === 'Width') { delete selection.width; delete selection.widthWaist; delete selection.widthAdjustment; }
                if (name === 'Construction') delete selection.construction;
                if (name === 'Flex Pattern') delete selection.flex;
            }
            fieldKeys[name].forEach(function (key) { if (value) selection[key] = value; else delete selection[key]; });
        });
        selection.shape = semanticString(params.get('kindred_shape')) || selection.shape || '';
        selection.productId = semanticString(expectedProductId) || selection.productId || '';
        selection.destination = window.location.pathname;
        return {
            shape: selection.shape,
            selection: selection,
            state: Object.assign({}, incomingBundle && incomingBundle.state || {}),
            fields: Object.keys(urlRecommendation).filter(function (name) { return semanticString(urlRecommendation[name]); }).map(function (name) {
                return { property: name, kind: name, groups: [name], value: semanticString(urlRecommendation[name]) };
            }),
            status: incomingStatus,
            pending: incomingStatus === 'pending',
            applied: incomingStatus === 'applied',
            failed: incomingStatus === 'failed',
            missing: incomingResult && incomingResult.missing ? incomingResult.missing.slice() : []
        };
    }

    function notifyIncoming() {
        document.dispatchEvent(new CustomEvent('kindred.sizing.handoff', { detail: incomingRecommendation(expectedProductId) }));
    }

    function retireIncomingRecommendation(options) {
        incomingRetired = true;
        incomingStatus = 'retired';
        urlComplete = true;
        if (urlRetryTimer) window.clearTimeout(urlRetryTimer);
        urlRetryTimer = null;
        if (urlAbortController) urlAbortController.abort();
        urlAbortController = null;
        if (hasUrlHandoff && (!options || options.cleanUrl !== false)) cleanTransferUrl();
        try {
            const saved = JSON.parse(window.sessionStorage.getItem(recommendationStorageKey) || 'null');
            if (saved && saved.origin === window.location.origin && saved.pathname === window.location.pathname.replace(/\/$/, '')) {
                window.sessionStorage.removeItem(recommendationStorageKey);
            }
        } catch (error) { /* Optional persistence. */ }
        notifyIncoming();
    }

    incomingBundle = readIncomingBundle();

    function readToolResult(root, suffix) {
        const element = root.querySelector(`[id$="ToolResult${suffix}"]`);

        return element ? element.textContent.trim() : '';
    }

    function recommendationFromTool(root) {
        const toolName = root.getAttribute('data-kindred-tool') || '';
        const youthTool = toolName === 'kidred' || toolName === 'elkhorn';
        const flexProfile = readToolResult(root, 'FlexProfile') ||
            (youthTool ? readToolResult(root, 'Flex') : toolName === 'procarve' ? 'Standard' : '');

        return {
            Function: readToolResult(root, 'BoardType'),
            Size: readToolResult(root, 'Length'),
            Width: readToolResult(root, 'Width'),
            Construction: readToolResult(root, 'Composite'),
            'Flex Profile': flexProfile
        };
    }

    function normalize(value) {
        return String(value || '')
            .toLowerCase()
            .replace(/fibreglass/g, 'fiberglass')
            .replace(/carbon\s*fibre/g, 'carbon fiber')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    function unique(values) {
        return Array.from(new Set(values.filter(Boolean)));
    }

    function widthCandidates(value) {
        const profile = String(value || '').split('·')[0].trim();
        const deltaMatch = String(value || '').match(/([+-]\s*\d+(?:\.\d+)?)\s*mm/i);
        const delta = deltaMatch ? deltaMatch[1].replace(/\s+/g, '') : '';

        return unique([
            value,
            profile,
            profile && delta ? `${profile} ${delta}mm` : '',
            delta ? `${delta}mm` : ''
        ]);
    }

    function candidatesFor(name, value) {
        const normalized = normalize(value);

        if (name === 'Product Type') {
            if (normalized.includes('split')) {
                return unique([value, 'Splitboard', 'Split']);
            }

            if (normalized.includes('ski')) {
                return unique([value, 'Ski', 'Skis']);
            }

            if (normalized.includes('snowboard') || normalized.includes('board')) {
                return unique([value, 'Snowboard', 'Solid Board', 'Solid']);
            }
        }

        if (name === 'Function') {
            return normalized.includes('split')
                ? ['Split +$300 CAD', 'Splitboard', 'Split']
                : ['Solid', 'Solid Board'];
        }

        if (name === 'Size') {
            const length = String(value || '').match(/\d+(?:\.\d+)?/);
            return unique([value, length && length[0], length && `${length[0]} cm`]);
        }

        if (name === 'Width') {
            return widthCandidates(value);
        }

        if (name === 'Flex Profile' || name === 'Flex Pattern') {
            if (normalized.includes('extra stiff') || normalized.includes('extra firm')) {
                return unique([value, 'Extra Stiff', 'Extra Firm', 'Extra Stiff Flex', 'Extra Firm Flex']);
            }

            if (normalized.includes('extra soft')) {
                return unique([value, 'Extra Soft', 'Extra Soft Flex']);
            }

            if (normalized.includes('firm') || normalized.includes('stiff')) {
                return unique([value, 'Firm', 'Stiff', 'Firm Flex', 'Stiff Flex']);
            }

            if (normalized.includes('soft')) {
                return unique([value, 'Soft', 'Soft Flex']);
            }

            if (normalized.includes('standard')) {
                return unique([value, 'Standard', 'Standard Flex', 'Regular']);
            }
        }

        if (name === 'Construction') {
            if (normalized.includes('race')) {
                return unique([value, 'Race Construction', 'Race Build +$449 CAD', 'Race Build']);
            }

            if (normalized.includes('titanal') && normalized.includes('carbon')) {
                return unique([
                    value,
                    'Titanal Reinforced Carbon Fiber',
                    'Titanal Reinforced Carbon',
                    'Titanal Reinforced Triaxial Carbon Fiber',
                    'Carbon Fiber/Titanal Edge',
                    'Carbon/Titanal Edge'
                ]);
            }

            if (normalized.includes('titanal') && normalized.includes('fiberglass')) {
                return unique([
                    value,
                    'Titanal Reinforced Fiberglass',
                    'Titanal Reinforced Triaxial Fiberglass',
                    'Fiberglass/Titanal Edge'
                ]);
            }

            if (normalized.includes('triaxial') && normalized.includes('fiberglass')) {
                return unique([value, 'Triaxial Fiberglass']);
            }

            if (normalized.includes('biaxial') && normalized.includes('fiberglass')) {
                return unique([value, 'Biaxial Fiberglass']);
            }

            if (normalized.includes('carbon')) {
                return unique([
                    value,
                    'Carbon Fiber',
                    'Carbon',
                    'Triaxial Carbon Fiber',
                    'Triaxial Carbon'
                ]);
            }

            if (normalized.includes('fiberglass')) {
                return unique([
                    value,
                    'Fiberglass',
                    'Triaxial Fiberglass',
                    'Biaxial Fiberglass'
                ]);
            }
        }

        return [value];
    }

    function candidatesWithGroupPrefixes(name, value) {
        const candidates = candidatesFor(name, value);
        const prefixes = name === 'Construction'
            ? ['Construction']
            : (name === 'Flex Profile' || name === 'Flex Pattern')
                ? ['Flex Pattern', 'Flex Profile', 'Flex', 'Stiffness']
                : [];
        const prefixed = candidates.slice();

        prefixes.forEach(function (prefix) {
            candidates.forEach(function (candidate) {
                prefixed.push(`${prefix}: ${candidate}`);
            });
        });

        return unique(prefixed);
    }

    function withoutTrailingPrice(value) {
        return String(value || '')
            .replace(/\s+\d+(?:\s+\d+)*(?:\s+(?:cad|usd))?$/, '')
            .trim();
    }

    function candidateMatcher(candidates, exactOptionName) {
        const wanted = candidates.map(normalize).filter(Boolean);
        const exactWanted = exactOptionName
            ? wanted.map(withoutTrailingPrice)
            : [];

        return function (value) {
            const raw = normalize(value);

            if (exactOptionName) {
                return exactWanted.indexOf(withoutTrailingPrice(raw)) !== -1;
            }

            return wanted.some(function (item) {
                return raw === item ||
                    raw.indexOf(`${item} `) === 0 ||
                    item.indexOf(`${raw} `) === 0;
            });
        };
    }

    function matchesOption(name, value, actual) {
        const semanticName = name === 'Flex' || name === 'Stiffness' ? 'Flex Profile' : name;
        if (semanticName === 'Size') {
            const desiredLength = String(value || '').match(/\d+(?:\.\d+)?/);
            const actualLength = String(actual || '').match(/\d+(?:\.\d+)?/);
            return Boolean(desiredLength && actualLength && Number(desiredLength[0]) === Number(actualLength[0]));
        }
        if (semanticName === 'Width') {
            const widthKey = function (text) {
                const normalized = normalize(text);
                if (normalized.includes('extra narrow')) return 'extra_narrow';
                if (normalized.includes('extra wide')) return 'extra_wide';
                if (normalized.includes('narrow')) return 'narrow';
                if (normalized.includes('wide')) return 'wide';
                if (normalized.includes('standard') || /\bstd\b/.test(normalized)) return 'standard';
                if (normalized.includes('custom')) return 'custom';
                return '';
            };
            const widthDelta = function (text) {
                const normalized = String(text || '').replace(/−/g, '-');
                const match = normalized.match(/([+-]\s*\d+(?:\.\d+)?)\s*mm/i) ||
                    normalized.match(/^\s*([+-]?\s*\d+(?:\.\d+)?)\s*(?:mm)?\s*$/i);
                return match ? Number(match[1].replace(/\s/g, '')) : null;
            };
            const desiredKey = widthKey(value);
            const actualKey = widthKey(actual);
            const desiredDelta = widthDelta(value);
            const actualDelta = widthDelta(actual);
            if (desiredDelta !== null && actualDelta !== null && desiredDelta !== actualDelta) return false;
            if (desiredKey && actualKey) return desiredKey === actualKey;
            if (desiredDelta !== null && actualDelta !== null) return desiredDelta === actualDelta;
            if (desiredKey === 'standard' && actualDelta === 0) return true;
        }
        return candidateMatcher(
            candidatesWithGroupPrefixes(semanticName, value),
            normalize(semanticName) === 'construction'
        )(actual);
    }

    function optionContext(element) {
        const group = element.closest(
            '[data-child-name], fieldset, [data-option-name], [data-name], ' +
            '.giftship-option-container, .giftship-option, .product-form__input, ' +
            '.option-selector, .product-option'
        );
        const label = element.id
            ? document.querySelector(`label[for="${CSS.escape(element.id)}"]`)
            : element.closest('label');
        const heading = group && group.querySelector(
            'legend, .label, .option-name, [data-option-label], .gs__label'
        );

        return [
            element.name,
            element.id,
            element.getAttribute('aria-label'),
            element.getAttribute('data-option-name'),
            group && group.getAttribute('data-child-name'),
            group && group.getAttribute('data-option-name'),
            group && group.getAttribute('data-name'),
            label && label.textContent,
            heading && heading.textContent
        ].map(normalize).join(' ');
    }

    function contextMatches(context, name) {
        const wanted = normalize(name);

        if (name === 'Construction') {
            return context.includes('composite') ||
                (!context.includes('touring construction') &&
                    (context.includes('construction') || context.includes('contruction')));
        }

        if (name === 'Flex Profile' || name === 'Flex Pattern') {
            return context.includes('flex') || context.includes('stiffness');
        }

        if (name === 'Function') {
            return context.includes('function') || context.includes('board format') || context.includes('touring construction');
        }

        if (name === 'Size') {
            return context.includes('size') || context.includes('length');
        }

        return context.includes(wanted);
    }

    function notifyOption(element) {
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function clickProductChoice(element) {
        // Native input/change events from an input.click() default action are
        // trusted too. Scope ownership to this synchronous click so they cannot
        // cancel our own transfer, while real customer edits between waits can.
        receiverChoiceDepth += 1;
        try { element.click(); }
        finally { receiverChoiceDepth -= 1; }
    }

    function optionLabel(element) {
        const namedValue = element.getAttribute('data-child-option-name');
        if (namedValue) return namedValue;
        if (element.tagName === 'OPTION') return element.textContent.trim() || element.value;
        return element.getAttribute('data-child-option-value') || choiceLabel(element) || element.value;
    }

    function optionMatches(element, name, value) {
        // Use the same named choice that productOptions exposes to the tools.
        // Giftship backing values may be IDs, and display text may only be a price.
        return matchesOption(name, value, optionLabel(element));
    }

    function availableSelectOption(option) {
        const group = option.closest('optgroup');
        return !option.hidden && enabledControl(option) &&
            !(group && (group.hidden || group.disabled));
    }

    function giftshipInputsWithin(scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;

        return productControlsWithin(contextRoot,
            'input.gs__product-element, input[data-child-option]'
        );
    }

    function giftshipGroupInputs(input, groupName, scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const inputs = controlsForGroup(contextRoot, groupName).filter(function (control) {
            return control.tagName === 'INPUT' && enabledControl(control);
        });
        const sameContext = function (candidate) {
            return contextMatches(optionContext(candidate), groupName);
        };
        const sameForm = function (candidate) {
            return !input.form || !candidate.form || candidate.form === input.form;
        };

        /* Native radio/checkbox names are the most precise group boundary. */
        if (input.name) {
            const sameName = inputs.filter(function (candidate) {
                return candidate.name === input.name && sameForm(candidate) && sameContext(candidate);
            });

            if (sameName.length > 1) return sameName;
        }

        /* Giftship sometimes exposes its group key on a wrapper instead. */
        const keyed = input.closest('[data-child-name], [data-option-name], [data-name]');
        if (keyed) {
            const key = keyed.getAttribute('data-child-name') ||
                keyed.getAttribute('data-option-name') || keyed.getAttribute('data-name');
            const attribute = keyed.hasAttribute('data-child-name')
                ? 'data-child-name'
                : keyed.hasAttribute('data-option-name') ? 'data-option-name' : 'data-name';
            const sameKey = inputs.filter(function (candidate) {
                const owner = candidate.closest(`[${attribute}]`);
                return owner && owner.getAttribute(attribute) === key &&
                    sameForm(candidate) && sameContext(candidate);
            });

            if (sameKey.length > 1) return sameKey;
        }

        /* Find the smallest semantic container holding more than one choice. */
        const groupSelector = [
            'fieldset',
            '[data-child-name]',
            '[data-option-name]',
            '[data-name]',
            '.giftship-option-container',
            '.product-form__input',
            '.option-selector',
            '.product-option'
        ].join(', ');
        let ancestor = input.parentElement;

        while (ancestor) {
            if (ancestor.matches && ancestor.matches(groupSelector)) {
                const descendants = inputs.filter(function (candidate) {
                    return ancestor.contains(candidate) && sameForm(candidate) && sameContext(candidate);
                });

                if (descendants.length > 1) return descendants;
            }

            if (ancestor === contextRoot || ancestor === document.body) break;
            ancestor = ancestor.parentElement;
        }

        /* Last resort: constrain the operation to Giftship controls with the same group label. */
        return inputs.filter(function (candidate) {
            return sameForm(candidate) && sameContext(candidate);
        });
    }

    function clearConflictingGiftshipInputs(input, groupName, matches, scope) {
        const conflicts = giftshipGroupInputs(input, groupName, scope).filter(function (candidate) {
            return candidate !== input && !candidate.disabled && candidate.checked &&
                !matches(candidate);
        });

        conflicts.forEach(function (candidate) {
            if (!candidate.isConnected || !candidate.checked) return;

            /* A checkbox click preserves any Giftship click handlers and native event order. */
            if (candidate.type === 'checkbox') {
                clickProductChoice(candidate);
                if (!candidate.isConnected || !candidate.checked) return;
            }

            /* Checked radios cannot be toggled off with click, so clear and notify explicitly. */
            candidate.checked = false;
            notifyOption(candidate);
        });

        return conflicts;
    }

    function applyProductOption(name, value, kind, scope) {
        const semanticName = kind || name;
        const matches = function (element) { return optionMatches(element, semanticName, value); };
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const controls = controlsForGroup(contextRoot, name).filter(enabledControl);
        const giftshipInputs = controls.filter(function (control) {
            return control.tagName === 'INPUT' && giftshipControl(control);
        });

        for (let i = 0; i < giftshipInputs.length; i += 1) {
            const input = giftshipInputs[i];
            if (!input.disabled && contextMatches(optionContext(input), name) && matches(input)) {
                clearConflictingGiftshipInputs(input, name, matches, contextRoot);
                if (!input.isConnected || input.disabled) {
                    return null;
                }
                if (!input.checked) {
                    clickProductChoice(input);
                }
                if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
                    continue;
                }
                if (!firstApplied) {
                    firstApplied = input;
                }
                return input;
            }
        }

        const selects = controls.filter(function (control) { return control.tagName === 'SELECT'; });

        for (let i = 0; i < selects.length; i += 1) {
            const select = selects[i];

            if (select.disabled || !contextMatches(optionContext(select), name)) {
                continue;
            }

            const option = Array.from(select.options).find(function (item) {
                return availableSelectOption(item) && matches(item);
            });

            if (option) {
                if (select.value !== option.value) {
                    select.value = option.value;
                    notifyOption(select);
                }
                if (!select.isConnected || select.value !== option.value) {
                    continue;
                }
                if (!firstApplied) {
                    firstApplied = select;
                }
                return select;
            }
        }

        const standardInputs = controls.filter(function (control) { return control.tagName === 'INPUT'; });

        for (let i = 0; i < standardInputs.length; i += 1) {
            const input = standardInputs[i];
            if (
                !input.disabled &&
                contextMatches(optionContext(input), name) &&
                matches(input)
            ) {
                clearConflictingGiftshipInputs(input, name, matches, contextRoot);
                if (!input.isConnected || !enabledControl(input) || !visibleControl(input, contextRoot)) return null;
                if (!input.checked) {
                    clickProductChoice(input);
                }
                if (!input.checked) {
                    continue;
                }
                if (!firstApplied) {
                    firstApplied = input;
                }
                return input;
            }
        }

        return null;
    }

    function selectedGiftshipMatches(entry, scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const semanticName = entry.kind || entry.property;
        const matches = function (element) { return optionMatches(element, semanticName, entry.value); };
        const matched = [];
        const seen = new Set();
        const availableControls = productControlsWithin(contextRoot,
            'select, input[type="radio"], input[type="checkbox"]', true
        );

        entry.groups.forEach(function (groupName) {
            const controls = controlsForGroup(contextRoot, groupName, availableControls).filter(enabledControl);
            controls.filter(function (control) { return control.tagName === 'INPUT'; }).forEach(function (input) {
                if (
                    !input.disabled && input.checked &&
                    contextMatches(optionContext(input), groupName) && matches(input) &&
                    !seen.has(input)
                ) {
                    seen.add(input);
                    matched.push(input);
                }
            });

            controls.filter(function (control) { return control.tagName === 'SELECT'; }).forEach(function (select) {
                const selected = select.options && select.options[select.selectedIndex];
                if (
                    !select.disabled && selected && availableSelectOption(selected) &&
                    contextMatches(optionContext(select), groupName) &&
                    matches(selected) &&
                    !seen.has(select)
                ) {
                    seen.add(select);
                    matched.push(select);
                }
            });
        });

        return matched;
    }

    function selectedGiftshipConflicts(entry, matches, scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const semanticName = entry.kind || entry.property;
        const desired = function (element) { return optionMatches(element, semanticName, entry.value); };
        const conflicts = [];
        const seen = new Set();

        entry.groups.forEach(function (groupName) {
            matches.filter(function (input) {
                return input && input.tagName === 'INPUT' &&
                    contextMatches(optionContext(input), groupName);
            }).forEach(function (input) {
                giftshipGroupInputs(input, groupName, contextRoot).forEach(function (candidate) {
                    if (
                        candidate !== input && !candidate.disabled && candidate.checked &&
                        !desired(candidate) && !seen.has(candidate)
                    ) {
                        seen.add(candidate);
                        conflicts.push(candidate);
                    }
                });
            });
        });

        return conflicts;
    }

    function verifyFields(fields, options) {
        const settings = options || {};
        const entries = (fields || []).filter(function (entry) {
            return entry && Boolean(entry.value) && entry.value !== '—' && entry.groups && entry.groups.length;
        });
        const context = productContext(settings.productId);
        const form = productForm(settings.productId);

        if ((settings.productId && !context) || !form) {
            return {
                ok: false,
                missing: entries.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
                missingEntries: entries.slice(),
                ambiguous: [],
                form: form
            };
        }

        const searchRoot = context || document;
        const missing = [];
        const ambiguous = [];

        entries.forEach(function (entry) {
            const matches = selectedGiftshipMatches(entry, searchRoot);
            const conflicts = selectedGiftshipConflicts(entry, matches, searchRoot);

            if (!matches.length) missing.push(entry);
            if (matches.length > 1 || conflicts.length) ambiguous.push(entry);
        });

        return {
            ok: missing.length === 0 && ambiguous.length === 0,
            missing: missing.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
            missingEntries: missing,
            ambiguous: ambiguous.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
            form: form
        };
    }

    function formProductContext(form) {
        return form && form.closest(
            '[data-kindred-commerce-context][data-product-id], ' +
            '[data-section-type="product"][data-product-id]'
        );
    }

    function formProductId(form) {
        const context = formProductContext(form);
        return context ? String(context.getAttribute('data-product-id') || '') : '';
    }

    function productFormScore(form) {
        let score = 0;

        if (form.querySelector('input.gs__product-element, input[data-child-option]')) score += 100;
        if (form.querySelector('#gsAppContainer, .gs__product-app-container')) score += 60;
        if (form.closest('[data-kindred-commerce-context]')) score += 30;
        if (form.closest('[data-section-type="product"]')) score += 20;
        if (form.classList.contains('product-single__form')) score += 10;
        return score;
    }

    function productForm(productId) {
        const expectedProductId = productId ? String(productId) : '';
        const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'))
            .filter(function (form) {
                if (form.closest(toolWrappers)) return false;
                // Giftship converts the main variant picker to id[] as soon as
                // an upsell checkbox changes. Keep the same product form after
                // that conversion, without treating an upsell-only form as a
                // native product form.
                const variantControls = Array.from(form.querySelectorAll(
                    'input[name="id"], select[name="id"], input[name="id[]"], select[name="id[]"]'
                ));
                if (!variantControls.some(function (control) {
                    return outsideTools(control) && !giftshipControl(control);
                })) return false;
                return !expectedProductId || formProductId(form) === expectedProductId;
            })
            .sort(function (left, right) {
                return productFormScore(right) - productFormScore(left);
            });

        return forms[0] || null;
    }

    function productContext(productId) {
        const form = productForm(productId);

        if (!form) return productId ? null : document;
        return formProductContext(form) || form;
    }

    function outsideTools(element) {
        return Boolean(element && !element.closest(toolWrappers));
    }

    function visibleControl(element, scope, styles) {
        if (!element || element.hidden) return false;
        // Choice inputs are often visually hidden in a clickable label. The
        // label and conditional group determine whether that choice is offered.
        const choiceInput = element.tagName === 'INPUT' &&
            (element.type === 'radio' || element.type === 'checkbox');
        let node = choiceInput ? (element.closest('label') || element.parentElement) : element;
        while (node && node.nodeType === 1) {
            if (node.hidden || node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true') return false;
            if (typeof window.getComputedStyle === 'function') {
                let style = styles && styles.get(node);
                if (!style) {
                    style = window.getComputedStyle(node);
                    if (styles) styles.set(node, style);
                }
                if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
            }
            if (node === scope) break;
            node = node.parentElement;
        }
        return true;
    }

    function enabledControl(element) {
        return Boolean(element && !element.disabled &&
            !(element.matches && element.matches(':disabled')) &&
            !element.closest('[aria-disabled="true"]'));
    }

    function productControlsWithin(scope, selector, includeDisabled, styles) {
        const context = scope && scope.querySelectorAll ? scope : document;
        const owner = context.nodeType === 1 ? formProductContext(context) : null;
        const form = context.tagName === 'FORM' ? context : productForm(owner && owner.getAttribute('data-product-id'));
        if (!form) return [];
        const visibilityStyles = styles || new Map();
        return Array.from(context.querySelectorAll(selector)).filter(function (control) {
            if (!outsideTools(control) || (control.form && control.form !== form)) return false;
            const controlOwner = formProductContext(control);
            if (controlOwner && controlOwner !== formProductContext(form)) return false;
            return (includeDisabled || enabledControl(control)) && visibleControl(control, context, visibilityStyles);
        });
    }

    function giftshipControl(control) {
        if (control.hasAttribute('data-variant-input')) return false;
        return Boolean(control.matches('.gs__product-element, [data-child-option]') ||
            control.closest('[data-child-name], #gsAppContainer, .gs__product-app-container, ' +
                '.giftship-option-container, .giftship-option') ||
            /^properties\[/.test(control.name || '') ||
            (control.tagName === 'SELECT' && control.querySelector('option[data-child-option-name]')));
    }

    function controlsForGroup(scope, name, availableControls, contexts) {
        const controls = (availableControls || productControlsWithin(scope,
            'select, input[type="radio"], input[type="checkbox"]', true
        )).filter(function (control) {
            return contextMatches(contexts ? contexts.get(control) : optionContext(control), name);
        });
        const giftshipControls = controls.filter(giftshipControl);
        // A native variant picker can mirror a Giftship group. It must not add
        // unavailable suggestions or count as a second selected app choice.
        return giftshipControls.length ? giftshipControls : controls;
    }

    function controlContexts(controls) {
        return new Map(controls.map(function (control) { return [control, optionContext(control)]; }));
    }

    function choiceLabel(input) {
        const label = input.closest('label') || (input.id
            ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
        return label ? label.textContent.trim() : '';
    }

    function productOptions(productId) {
        const names = ['Size', 'Width', 'Construction', 'Flex', 'Function'];
        const groups = {};
        names.forEach(function (name) { groups[name] = { present: false, values: [], entries: [] }; });
        const form = productForm(productId);
        const context = form && productContext(productId);
        const styles = new Map();
        if (form && context && visibleControl(form, context, styles)) {
            const availableControls = productControlsWithin(context,
                'select, input[type="radio"], input[type="checkbox"]', true, styles
            );
            const contexts = controlContexts(availableControls);
            const controls = [];
            names.forEach(function (name) {
                controlsForGroup(context, name === 'Flex' ? 'Flex Profile' : name, availableControls, contexts).forEach(function (control) {
                    if (!controls.includes(control)) controls.push(control);
                });
            });
            controls.forEach(function (control) {
                const groupName = names.find(function (name) {
                    return contextMatches(contexts.get(control), name === 'Flex' ? 'Flex Profile' : name);
                });
                if (!groupName) return;
                const group = groups[groupName];
                group.present = true;
                if (!enabledControl(control)) return;
                const add = function (value, label) {
                    const actualValue = String(value || '').trim();
                    const actualLabel = String(label || actualValue).trim();
                    if (!actualValue || !actualLabel || /^(?:please\s+)?(?:choose|select)(?:\s|$)/i.test(actualLabel)) return;
                    if (!group.values.includes(actualLabel)) group.values.push(actualLabel);
                    if (!group.entries.some(function (entry) { return entry.value === actualValue && entry.label === actualLabel; })) {
                        group.entries.push({ value: actualValue, label: actualLabel });
                    }
                };
                if (control.tagName === 'SELECT') {
                    Array.from(control.options).forEach(function (option) {
                        if (!availableSelectOption(option)) return;
                        add(option.value, optionLabel(option));
                    });
                } else {
                    const namedValue = control.getAttribute('data-child-option-name') ||
                        control.getAttribute('data-child-option-value');
                    add(control.value || namedValue, optionLabel(control));
                }
            });
        }
        const busy = productOptionsBusy(context, styles);
        const ready = Boolean(form && context && groups.Size.values.length && !busy);
        const signature = JSON.stringify({ productId: String(productId || formProductId(form) || ''), ready: ready, groups: groups });
        return { ready: ready, busy: Boolean(busy), groups: groups, signature: signature };
    }

    function productOptionsBusy(context, styles) {
        const busySelector =
            '[data-giftship-loading="true"], #gsAppContainer[aria-busy="true"], ' +
            '.gs__product-app-container[aria-busy="true"], #gsAppContainer[data-loading="true"], ' +
            '.gs__product-app-container[data-loading="true"]';
        if (!context) return false;
        if ((context.matches && context.matches(busySelector)) ||
            Array.from(context.querySelectorAll(busySelector)).some(function (element) {
                return outsideTools(element) && visibleControl(element, context, styles);
            })) return true;
        const indicators = Array.from(context.querySelectorAll('.gs__loading, .giftship-loading')).filter(function (element) {
            return outsideTools(element) && visibleControl(element, context, styles);
        });
        if (!indicators.length) return false;
        // A price or cart-summary spinner can remain visible after choices are
        // ready. It must not hold an enabled product choice open indefinitely.
        const hasChoices = productControlsWithin(context, 'select, input[type="radio"], input[type="checkbox"]', false, styles).some(giftshipControl);
        return indicators.some(function (element) {
            const group = element.closest('[data-child-name], [data-option-name], [data-name], .giftship-option-container, .giftship-option');
            const label = group && optionContext(element);
            return !hasChoices || (label && ['Size', 'Width', 'Construction', 'Flex Profile', 'Function'].some(function (name) {
                return contextMatches(label, name);
            }));
        });
    }

    function setProperty(form, name, value) {
        let input = form.querySelector(`input[data-kindred-handoff="${CSS.escape(name)}"]`);

        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${name}]`;
            input.dataset.kindredHandoff = name;
            form.appendChild(input);
        }

        input.value = value;
    }

    function applicationRun(settings) {
        const requested = Number(settings.timeoutMs);
        const timeout = Number.isFinite(requested) && requested > 0
            ? Math.max(250, Math.min(20000, requested)) : 15000;
        const key = String(settings.productId || formProductId(productForm()) || 'main');
        const previous = activeApplications.get(key);
        if (previous) previous.cancel();
        const pendingWaits = new Set();
        const run = {
            deadline: Date.now() + timeout,
            cancelled: false,
            timedOut: false,
            signal: settings.signal,
            cancel: function () {
                run.cancelled = true;
                pendingWaits.forEach(function (finish) { finish(); });
            },
            active: function () {
                if (run.signal && run.signal.aborted) run.cancel();
                if (Date.now() >= run.deadline) run.timedOut = true;
                return !run.cancelled && !run.timedOut;
            },
            wait: function (ms) {
                if (!run.active()) return Promise.resolve();
                return new Promise(function (resolve) {
                    let timer;
                    const finish = function () {
                        window.clearTimeout(timer);
                        pendingWaits.delete(finish);
                        resolve();
                    };
                    pendingWaits.add(finish);
                    timer = window.setTimeout(finish, Math.max(0, Math.min(ms, run.deadline - Date.now())));
                });
            },
            finish: function () {
                pendingWaits.forEach(function (finish) { finish(); });
                if (run.signal && run.signal.removeEventListener) run.signal.removeEventListener('abort', run.cancel);
                if (activeApplications.get(key) === run) activeApplications.delete(key);
            }
        };
        if (run.signal && run.signal.addEventListener) run.signal.addEventListener('abort', run.cancel, { once: true });
        activeApplications.set(key, run);
        return run;
    }

    async function waitForProductOptions(productId, entries, run, maxWait) {
        const deadline = Math.min(run.deadline, Date.now() + (maxWait || 1800));
        let previous = '';
        let previousContext = null;
        let previousForm = null;
        let stableSince = 0;
        while (run.active() && Date.now() < deadline) {
            // Giftship and the variant picker can replace the whole form.
            const context = productContext(productId);
            const form = productForm(productId);
            if (context && form && !productOptionsBusy(context)) {
                const availableControls = productControlsWithin(context,
                    'select, input[type="radio"], input[type="checkbox"]', true
                );
                const contexts = controlContexts(availableControls);
                const relevant = availableControls.filter(function (control) {
                    return entries.some(function (entry) {
                        return entry.groups.some(function (name) { return contextMatches(contexts.get(control), name); });
                    });
                });
                // Read controls once per sample. Rebuilding the complete public
                // inventory for every group repeatedly forces expensive style reads.
                const signature = JSON.stringify(relevant.map(function (control) {
                    if (control.tagName === 'SELECT') {
                        return [contexts.get(control), control.name, control.value, control.disabled,
                            Array.from(control.options).map(function (option) {
                                return [option.value, optionLabel(option), availableSelectOption(option)];
                            })];
                    }
                    return [contexts.get(control), control.name, control.value, optionLabel(control), control.checked, control.disabled];
                }));
                if (signature === previous && context === previousContext && form === previousForm) {
                    if (Date.now() - stableSince >= 600) return true;
                } else {
                    previous = signature;
                    previousContext = context;
                    previousForm = form;
                    stableSince = Date.now();
                }
            } else {
                previous = '';
                stableSince = 0;
            }
            await run.wait(Math.min(120, deadline - Date.now()));
        }
        return false;
    }

    function cleanTransferUrl() {
        const cleanUrl = new URL(window.location.href);

        transferKeys.forEach(function (key) {
            cleanUrl.searchParams.delete(key);
        });

        if (cleanUrl.hash === '#kindred-product-options') {
            cleanUrl.hash = '';
        }

        window.history.replaceState(
            {},
            document.title,
            cleanUrl.pathname + cleanUrl.search + cleanUrl.hash
        );
    }

    function showStatus(recommendation, appliedCount, missing, embeddedStatus) {
        const attached = Boolean(productForm() && missing.length);
        const fullyApplied = appliedCount > 0 && missing.length === 0;
        const partiallyApplied = appliedCount > 0 && missing.length > 0;
        const missingNames = missing.map(function (entry) { return entry[0]; });

        if (embeddedStatus) {
            if (fullyApplied) {
                embeddedStatus.textContent = 'The recommended product options were applied.';
            } else if (partiallyApplied && attached) {
                embeddedStatus.textContent = `Available options were applied. Please confirm: ${missingNames.join(', ')}.`;
            } else if (attached) {
                embeddedStatus.textContent = 'Your recommendation is ready for confirmation.';
            } else {
                embeddedStatus.textContent = 'The recommended options could not be applied. Please review your selections and try again.';
            }

            return embeddedStatus;
        }

        const existing = document.getElementById('kindred-sizing-handoff-status');
        const status = existing || document.createElement('div');
        const values = Object.entries(recommendation)
            .filter(function (entry) { return Boolean(entry[1]); })
            .map(function (entry) { return `${entry[0]}: ${entry[1]}`; });
        const host = document.querySelector(
            '[data-section-type="product"] .product-single__meta, ' +
            '[data-section-type="product"], .product-single__meta'
        ) || productForm();

        status.id = 'kindred-sizing-handoff-status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.style.cssText = [
            'margin:0 0 20px',
            'padding:14px 16px',
            'border:1px solid #9ec7d8',
            'border-radius:10px',
            'background:#eaf7fc',
            'color:#111',
            'font-size:14px',
            'line-height:1.45'
        ].join(';');
        status.textContent = '';
        const lead = document.createElement('strong');
        lead.textContent = fullyApplied
            ? 'Your selections were applied.'
            : partiallyApplied
                ? 'Available selections were applied.'
                : 'Your selections were carried over.';
        status.appendChild(lead);
        status.appendChild(document.createTextNode(
            ` ${values.join(' · ')}${attached ? ` ${missingNames.join(', ')} still ${missingNames.length === 1 ? 'needs' : 'need'} confirmation.` : ''}`
        ));

        if (!existing && host) {
            host.insertBefore(status, host.firstChild);
        }

        return status;
    }

    async function applyFields(fields, options) {
        const settings = options || {};
        if (settings.userInitiated) {
            // A deliberate on-page choice takes ownership from an incoming
            // URL recommendation, including its already scheduled retry.
            retireIncomingRecommendation();
        }
        const entries = (fields || []).filter(function (entry) {
            return entry && Boolean(entry.value) && entry.value !== '—' && entry.groups && entry.groups.length;
        });
        const missing = [];
        const ambiguous = [];
        const appliedElements = [];
        let appliedCount = 0;
        const displayValues = {};

        entries.forEach(function (entry) {
            displayValues[entry.property || entry.kind || entry.groups[0]] = entry.value;
        });

        firstApplied = null;
        const run = applicationRun(settings);
        try {
            let controlsReady = false;
            let settled = true;
            const initialDeadline = Math.min(run.deadline, Date.now() + 3000);
            while (run.active() && Date.now() < initialDeadline) {
                const searchRoot = productContext(settings.productId);
                if (searchRoot && !productOptionsBusy(searchRoot)) {
                    const controls = productControlsWithin(searchRoot, 'select, input[type="radio"], input[type="checkbox"]');
                    controlsReady = controls.some(function (control) {
                        const context = optionContext(control);
                        return entries.some(function (entry) {
                            return entry.groups.some(function (name) { return contextMatches(context, name); });
                        });
                    });
                    // Function can legitimately be offered before a conditional
                    // Size group exists, including when every app choice is a select.
                    if (controlsReady) break;
                }
                await run.wait(Math.min(100, initialDeadline - Date.now()));
            }
            if (!controlsReady && !run.cancelled) run.timedOut = true;

            const appliedEntries = new Set();
            async function applyEntry(entry) {
                let applied = null;
                let changed = false;
                const entryDeadline = Math.min(run.deadline, Date.now() + 1800);
                while (run.active() && Date.now() < entryDeadline && !applied) {
                    const searchRoot = productContext(settings.productId);
                    if (searchRoot && !productOptionsBusy(searchRoot)) {
                        const selected = selectedGiftshipMatches(entry, searchRoot);
                        if (selected.length === 1 && !selectedGiftshipConflicts(entry, selected, searchRoot).length) {
                            applied = selected[0];
                        } else {
                            for (let groupIndex = 0; groupIndex < entry.groups.length && !applied && run.active(); groupIndex += 1) {
                                applied = applyProductOption(entry.groups[groupIndex], entry.value, entry.kind, searchRoot);
                            }
                            changed = Boolean(applied);
                        }
                    }
                    if (!applied) {
                        await run.wait(Math.min(125, entryDeadline - Date.now()));
                    }
                }

                if (applied) {
                    appliedEntries.add(entry);
                    if (!firstApplied) firstApplied = applied;
                    if (changed) settled = await waitForProductOptions(settings.productId, entries, run);
                }
                return applied;
            }

            for (let i = 0; i < entries.length && run.active(); i += 1) await applyEntry(entries[i]);

            if (settings.verify && run.active()) {
                settled = await waitForProductOptions(settings.productId, entries, run);
                // A later app choice can reload and reset earlier choices. Reapply
                // only fields that were available, then verify the current controls.
                for (let pass = 0; pass < 2 && run.active(); pass += 1) {
                    const verification = verifyFields(entries, settings);
                    if (verification.ok) break;
                    const retry = entries.filter(function (entry) {
                        const name = entry.property || entry.kind || entry.groups[0];
                        return appliedEntries.has(entry) &&
                            (verification.missing.indexOf(name) !== -1 || verification.ambiguous.indexOf(name) !== -1);
                    });
                    if (!retry.length) break;
                    for (let i = 0; i < retry.length && run.active(); i += 1) await applyEntry(retry[i]);
                    if (run.active()) settled = await waitForProductOptions(settings.productId, entries, run);
                }
            }

            const searchRoot = productContext(settings.productId);
            run.active();
            const unconfirmed = !controlsReady || !settled || run.cancelled || run.timedOut || productOptionsBusy(searchRoot);
            entries.forEach(function (entry) {
                const selected = searchRoot ? selectedGiftshipMatches(entry, searchRoot) : [];
                if (unconfirmed || !appliedEntries.has(entry) || (settings.verify && !selected.length)) missing.push(entry);
                if (settings.verify && (selected.length > 1 || selectedGiftshipConflicts(entry, selected, searchRoot).length)) ambiguous.push(entry);
                if (selected.length) appliedElements.push(selected[0]);
            });
            appliedCount = appliedEntries.size;
            firstApplied = appliedElements[0] || null;

            const form = productForm(settings.productId);

            if (form && settings.attachMissing !== false && !unconfirmed) {
                missing.forEach(function (entry) {
                    setProperty(form, entry.property || entry.kind || entry.groups[0], entry.value);
                });
            }

            const missingPairs = missing.map(function (entry) {
                return [entry.property || entry.kind || entry.groups[0], entry.value];
            });
            const status = settings.showStatus === false || run.cancelled ? null : showStatus(
                displayValues,
                appliedCount,
                missingPairs,
                settings.embeddedStatus || null
            );

            if (settings.cleanUrl && !unconfirmed) {
                cleanTransferUrl();
            }
            if (!run.cancelled) document.dispatchEvent(new CustomEvent('kindred.sizing.applied', {
                detail: {
                    applied: appliedCount,
                    attached: form && settings.attachMissing !== false && !unconfirmed ? missing.length : 0,
                    missing: missingPairs.map(function (entry) { return entry[0]; }),
                    shape: settings.shape || params.get('kindred_shape')
                }
            }));

            if (settings.scroll !== false && !run.cancelled) window.setTimeout(function () {
                const destination = firstApplied || status;

                if (destination) {
                    destination.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 250);

            return {
                ok: missing.length === 0 && ambiguous.length === 0 && Boolean(form),
                applied: appliedCount,
                appliedElements: appliedElements,
                missing: missingPairs.map(function (entry) { return entry[0]; }),
                missingEntries: missing,
                ambiguous: ambiguous.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
                form: form,
                cancelled: run.cancelled,
                timedOut: run.timedOut || (!settled && !run.cancelled)
            };
        } finally {
            run.finish();
        }
    }

    function applyRecommendation(recommendation, options) {
        const fields = Object.entries(recommendation || {})
            .filter(function (entry) { return Boolean(entry[1]) && entry[1] !== '—'; })
            .map(function (entry) {
                return {
                    property: entry[0],
                    kind: entry[0],
                    groups: [entry[0]],
                    value: entry[1]
                };
            });
        return applyFields(fields, options);
    }

    function scheduleUrlRetry() {
        if (urlComplete || urlRetryTimer || (urlMaintenanceDeadline
            ? urlRepairAttempts >= 3 || Date.now() >= urlMaintenanceDeadline
            : urlAttempts >= 5)) return;
        urlRetryTimer = window.setTimeout(function () {
            urlRetryTimer = null;
            startUrlHandoff();
        }, Math.min(2200, 400 + (urlAttempts * 350)));
    }

    function startUrlHandoff() {
        if (!hasUrlHandoff || incomingRetired) return null;
        if (urlInFlight) return urlInFlight;
        const maintenance = Boolean(urlMaintenanceDeadline);
        if (maintenance) {
            // Some app initialization repaints happen after the first stable
            // confirmation. Keep the carried setup for those short-lived reloads.
            // A customer's edit retires this ownership immediately.
            const incoming = incomingRecommendation(expectedProductId);
            const context = productContext(expectedProductId);
            const verification = incoming && verifyFields(incoming.fields, { productId: expectedProductId });
            const busy = productOptionsBusy(context);
            if (verification && verification.ok && !busy) {
                if (!urlComplete || incomingStatus !== 'applied') {
                    urlComplete = true;
                    incomingStatus = 'applied';
                    incomingResult = verification;
                    if (urlRetryTimer) window.clearTimeout(urlRetryTimer);
                    urlRetryTimer = null;
                    notifyIncoming();
                }
                return null;
            }
            if (Date.now() >= urlMaintenanceDeadline || urlRepairAttempts >= 3) {
                incomingStatus = 'failed';
                incomingResult = verification || { missing: [] };
                if (busy && incoming) incomingResult.missing = incoming.fields.map(function (entry) { return entry.property; });
                else incomingResult.missing = unique((incomingResult.missing || []).concat(incomingResult.ambiguous || []));
                notifyIncoming();
                return null;
            }
            urlRepairAttempts += 1;
            urlComplete = false;
        } else if (urlComplete) return null;

        urlAttempts += 1;
        incomingStatus = 'pending';
        urlAbortController = typeof window.AbortController === 'function' ? new window.AbortController() : null;
        urlInFlight = applyRecommendation(urlRecommendation, {
            attachMissing: false,
            cleanUrl: false,
            productId: expectedProductId,
            shape: params.get('kindred_shape'),
            verify: true,
            signal: urlAbortController && urlAbortController.signal,
            scroll: false
        }).then(function (result) {
            if (incomingRetired || result.cancelled) return result;
            incomingResult = result;
            if (result.ok) {
                urlComplete = true;
                incomingStatus = 'applied';
                if (!urlMaintenanceDeadline) urlMaintenanceDeadline = Date.now() + 60000;
                cleanTransferUrl();
                notifyIncoming();
                return result;
            }
            incomingStatus = (maintenance ? urlRepairAttempts >= 3 : urlAttempts >= 5) ? 'failed' : 'pending';
            notifyIncoming();
            scheduleUrlRetry();
            return result;
        }).catch(function (error) {
            if (incomingRetired) return null;
            incomingStatus = (maintenance ? urlRepairAttempts >= 3 : urlAttempts >= 5) ? 'failed' : 'pending';
            notifyIncoming();
            scheduleUrlRetry();
            if (window.console && typeof window.console.warn === 'function') {
                window.console.warn('Kindred sizing handoff will retry after an option-loading error.', error);
            }
            return null;
        }).finally(function () {
            urlInFlight = null;
            urlAbortController = null;
        });

        notifyIncoming();
        return urlInFlight;
    }

    document.addEventListener('click', function (event) {
        const target = event.target instanceof Element ? event.target : null;
        const applyButton = target && target.closest(
            '.kindred-sizing-tool [data-apply-giftship]'
        );

        if (!applyButton || applyButton.disabled || applyButton.dataset.kindredApplying === 'true') {
            return;
        }

        const root = applyButton.closest('.kindred-sizing-tool');

        if (!root) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        applyButton.dataset.kindredApplying = 'true';
        applyButton.disabled = true;

        const embeddedStatus = root.querySelector('[id$="ToolGiftshipStatus"]');

        if (embeddedStatus) {
            embeddedStatus.textContent = 'Applying your recommendation…';
        }

        applyRecommendation(recommendationFromTool(root), {
            cleanUrl: false,
            userInitiated: true,
            embeddedStatus: embeddedStatus,
            shape: root.getAttribute('data-kindred-tool') || ''
        }).catch(function () {
            if (embeddedStatus) {
                embeddedStatus.textContent = 'The recommendation could not be applied. Please try again.';
            }
        }).finally(function () {
            applyButton.disabled = false;
            delete applyButton.dataset.kindredApplying;
        });
    }, true);

    window.KindredGiftshipHandoff = Object.assign(window.KindredGiftshipHandoff || {}, {
        applyRecommendation: applyRecommendation,
        applyFields: applyFields,
        verifyFields: verifyFields,
        productContext: productContext,
        productForm: productForm,
        productOptions: productOptions,
        matchesOption: matchesOption,
        setProperty: setProperty,
        carryRecommendation: carryRecommendation,
        incomingRecommendation: incomingRecommendation,
        applyIncomingRecommendation: startUrlHandoff,
        retireIncomingRecommendation: retireIncomingRecommendation
    });
    window.KindredGiftshipHandoffTest = Object.assign(window.KindredGiftshipHandoffTest || {}, {
        matchesOption: matchesOption,
        productOptions: productOptions
    });

    if (hasUrlHandoff) {
        document.addEventListener('change', function (event) {
            // Receiver-owned clicks can produce trusted native change events.
            // A customer's real product-option edit owns the choices from here on.
            if (incomingRetired || receiverChoiceDepth || !event.isTrusted || !event.target || !outsideTools(event.target)) return;
            const context = productContext(expectedProductId);
            if (context && context.contains(event.target)) retireIncomingRecommendation();
        });
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', startUrlHandoff, { once: true });
        } else {
            startUrlHandoff();
        }

        document.addEventListener('giftship.loaded', startUrlHandoff);
        document.addEventListener('giftship.reloaded', startUrlHandoff);
    }
})();
